import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  FiShield,
  FiPlus,
  FiTrash2,
  FiAlertTriangle,
  FiInfo,
  FiWifi,
} from 'react-icons/fi';
import { ipWhitelistService } from '../../services/apiService';
import { useAuthStore } from '../../utils/store';
import { useT } from '../../utils/i18n';
import { formatISTDate } from '../../utils/dateUtils';

const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;

function isValidIp(ip) {
  if (!ip) return false;
  if (IPV4_RE.test(ip)) {
    return ip.split('.').every(n => Number(n) <= 255);
  }
  // Accept anything else that looks like an IPv6 address (basic check)
  return ip.includes(':') && ip.length <= 45;
}

export const IpWhitelistSettings = () => {
  const t   = useT();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [settings, setSettings] = useState({ enabled: false, ips: [] });
  const [myIp,     setMyIp]     = useState('');
  const [newIp,    setNewIp]    = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [ipError,  setIpError]  = useState('');
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    Promise.all([
      ipWhitelistService.getSettings(),
      ipWhitelistService.getMyIp(),
    ])
      .then(([s, ipData]) => {
        setSettings(s || { enabled: false, ips: [] });
        setMyIp(ipData.ip || '');
      })
      .catch(e => toast.error(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  const handleToggle = () => {
    setSettings(prev => ({ ...prev, enabled: !prev.enabled }));
  };

  const handleAddIp = () => {
    const trimmed = newIp.trim();
    if (!trimmed) { setIpError(t('ipWhitelist.ipRequired')); return; }
    if (!isValidIp(trimmed)) { setIpError(t('ipWhitelist.ipInvalid')); return; }
    if (settings.ips.some(e => e.ip === trimmed)) {
      setIpError(t('ipWhitelist.alreadyAdded'));
      return;
    }
    setSettings(prev => ({
      ...prev,
      ips: [...prev.ips, { ip: trimmed, label: newLabel.trim(), addedAt: new Date().toISOString() }],
    }));
    setNewIp('');
    setNewLabel('');
    setIpError('');
  };

  const handleAddMyIp = () => {
    if (!myIp) return;
    if (settings.ips.some(e => e.ip === myIp)) {
      toast.info(t('ipWhitelist.alreadyAdded'));
      return;
    }
    setSettings(prev => ({
      ...prev,
      ips: [...prev.ips, { ip: myIp, label: t('ipWhitelist.myIpLabel'), addedAt: new Date().toISOString() }],
    }));
  };

  const handleRemove = (ip) => {
    setSettings(prev => ({ ...prev, ips: prev.ips.filter(e => e.ip !== ip) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await ipWhitelistService.updateSettings(settings);
      setSettings(updated);
      toast.success(t('ipWhitelist.saved'));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
        <FiShield size={40} className="text-gray-300" />
        <p className="text-sm">{t('settings.adminOnly')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        {t('settings.loadingPrompts')}
      </div>
    );
  }

  const myIpAlreadyAdded = myIp && settings.ips.some(e => e.ip === myIp);
  const enabledWithNoIps = settings.enabled && settings.ips.length === 0;
  const isLoopback = myIp === '127.0.0.1' || myIp === '::1';

  return (
    <div className="space-y-5 max-w-2xl">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
          <FiShield size={20} className="text-primary-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('ipWhitelist.title')}</h1>
          <p className="text-sm text-gray-500">{t('ipWhitelist.subtitle')}</p>
        </div>
      </div>

      {/* Admin exemption notice */}
      <div className="flex items-start gap-2.5 px-3.5 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
        <FiInfo size={15} className="mt-0.5 flex-shrink-0 text-blue-500" />
        <span>{t('ipWhitelist.adminNotAffected')}</span>
      </div>

      {/* Enable toggle card */}
      <div className="card p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-800">{t('ipWhitelist.enableToggle')}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {settings.enabled ? t('ipWhitelist.enabledDesc') : t('ipWhitelist.disabledDesc')}
            </p>
          </div>
          <button
            onClick={handleToggle}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
              settings.enabled ? 'bg-primary-500' : 'bg-gray-300'
            }`}
            role="switch"
            aria-checked={settings.enabled}
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                settings.enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Warning: enabled but no IPs */}
        {enabledWithNoIps && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <FiAlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-amber-500" />
            <span>{t('ipWhitelist.warningEmpty')}</span>
          </div>
        )}
      </div>

      {/* Current IP card */}
      <div className="card p-4 space-y-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('ipWhitelist.yourIp')}</p>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FiWifi size={14} className="text-gray-400 flex-shrink-0" />
            <span className="font-mono text-sm text-gray-800 select-all">
              {myIp || '—'}
            </span>
          </div>
          {!isLoopback && (
            <button
              onClick={handleAddMyIp}
              disabled={!myIp || myIpAlreadyAdded}
              className="btn btn-ghost text-xs text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
            >
              {myIpAlreadyAdded ? t('ipWhitelist.alreadyAdded') : t('ipWhitelist.addMyIp')}
            </button>
          )}
        </div>
        {isLoopback && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
            {t('ipWhitelist.loopbackHint')}
          </p>
        )}
      </div>

      {/* Add IP form */}
      <div className="card p-4 space-y-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('ipWhitelist.addIp')}</p>
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          <div className="flex-1 min-w-0 space-y-1">
            <input
              type="text"
              value={newIp}
              onChange={e => { setNewIp(e.target.value); setIpError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleAddIp()}
              placeholder={t('ipWhitelist.ipPlaceholder')}
              className={`input w-full text-sm font-mono ${ipError ? 'border-danger-400' : ''}`}
            />
            {ipError && <p className="text-xs text-danger-600">{ipError}</p>}
          </div>
          <input
            type="text"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddIp()}
            placeholder={t('ipWhitelist.labelPlaceholder')}
            className="input text-sm sm:w-44"
          />
          <button
            onClick={handleAddIp}
            className="btn btn-primary flex items-center gap-1.5 text-sm flex-shrink-0"
          >
            <FiPlus size={14} />
            {t('ipWhitelist.addIp')}
          </button>
        </div>
      </div>

      {/* IP list */}
      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {t('ipWhitelist.allowedIps')} ({settings.ips.length})
          </p>
        </div>
        {settings.ips.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">{t('ipWhitelist.noIps')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="px-4 py-2.5 font-medium">{t('ipWhitelist.ipAddress')}</th>
                <th className="px-4 py-2.5 font-medium hidden sm:table-cell">{t('ipWhitelist.labelCol')}</th>
                <th className="px-4 py-2.5 font-medium hidden md:table-cell">{t('ipWhitelist.addedOn')}</th>
                <th className="px-4 py-2.5 w-12" />
              </tr>
            </thead>
            <tbody>
              {settings.ips.map((entry, i) => (
                <tr key={entry.ip + i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-mono text-gray-800 text-xs">{entry.ip}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs hidden sm:table-cell">
                    {entry.label || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell">
                    {entry.addedAt ? formatISTDate(entry.addedAt) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleRemove(entry.ip)}
                      title={t('ipWhitelist.remove')}
                      className="text-gray-400 hover:text-danger-600 transition"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center gap-1.5"
        >
          <FiShield size={14} />
          {saving ? t('ipWhitelist.saving') : t('ipWhitelist.saveChanges')}
        </button>
        {enabledWithNoIps && (
          <p className="text-xs text-amber-600">{t('ipWhitelist.warningEmptyShort')}</p>
        )}
      </div>
    </div>
  );
};
